import { Color } from '@persona-id/design-tokens';
import { EyeIcon, EyeOffIcon, GraphIcon } from '@persona-id/design-tokens/icons';
import { kebabCase } from 'lodash/fp';
import { useCallback, useEffect, useRef, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { Link } from 'react-router-dom-v5-compat';
import styled from 'styled-components';

import { Mode } from 'dashboard/components/GraphCanvasV2/behaviors';
import GraphCanvasContextProvider from 'dashboard/components/GraphModule/GraphCanvasContextProvider';
import GraphConfigurationProvider from 'dashboard/components/GraphModule/GraphConfigurationProvider';
import GraphDataWrapper from 'dashboard/components/GraphModule/GraphDataWrapper';
import GraphQueryingProvider from 'dashboard/components/GraphModule/GraphQueryingProvider';
import { useBackend } from 'dashboard/components/GraphModule/hooks/useBackend';
import { useG6Wrapper } from 'dashboard/components/GraphModule/hooks/useG6Wrapper';
import { useGraphData } from 'dashboard/components/GraphModule/hooks/useGraphData';
import { useGraphExplorer } from 'dashboard/components/GraphModule/hooks/useGraphExplorer';
import { useGraphQuerying } from 'dashboard/components/GraphModule/hooks/useGraphQuerying';
import GraphV2Card from 'dashboard/components/GraphV2Card';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { Account } from 'dashboard/models/account';
import { AccountStatus } from 'dashboard/models/account-status';
import { AccountType } from 'dashboard/models/account-type';
import { Case } from 'dashboard/models/case';
import { CaseQueryResult } from 'dashboard/models/case-query-result';
import { Environment } from 'dashboard/models/environment';
import { Graph, NodeId } from 'dashboard/models/graph';
import { GraphViewTypes } from 'dashboard/models/graph-explorer';
import {
  Creator,
  generateVariableMap,
  GraphQuery,
  GraphQueryStatus,
  NHopsFromAccountQuery,
  Operator,
  ValueType,
  VariableValue,
} from 'dashboard/models/graph-query';
import { GraphQueryTemplate } from 'dashboard/models/graph-query-template';
import { GraphSnapshot } from 'dashboard/models/graph-snapshot';
import { ProductFeatureName } from 'dashboard/models/organization';
import { User } from 'dashboard/models/user';
import { ModuleGraphV2 } from 'dashboard/modules/GraphV2';
import { generateGraphQueryInfo, GraphQueryInfo } from 'dashboard/modules/GraphV2/GraphQueryInfo';
import { GraphQueryOptionSelector } from 'dashboard/modules/GraphV2/GraphQueryOptionSelector';
import NavigationHeader from 'dashboard/modules/GraphV2/NavigationHeader';
import { isGraphQueryCaseObjectOption, StateAndContext } from 'dashboard/modules/GraphV2/types';
import {
  getRunnableGraphQueryTemplates,
  isNHopWithOneAccountVariable,
} from 'dashboard/modules/GraphV2/utils';
import Placeholder from 'dashboard/modules/Placeholder';
import { showMessage } from 'dashboard/redux/actions/messages';
import { RootState } from 'dashboard/redux/store';
import { loadGraphsAndGraph } from 'dashboard/redux/thunk-actions/graph';
import { runGraphQuery } from 'dashboard/redux/thunk-actions/graph-query';
import { loadGraphQueryTemplates } from 'dashboard/redux/thunk-actions/graph-query-templates';
import Button from 'design-system/components/Button';
import { Paragraph } from 'design-system/components/Text';
import Tooltip from 'design-system/components/Tooltip';
import useApiLoader from 'lib/hooks/useApiLoader';
import useFeatureFlag from 'lib/hooks/useFeatureFlag';
import useProductFeature from 'lib/hooks/useProductFeature';
import { isPermissionsError, permissionsErrorMessage } from 'lib/permissions-errors';

const getCurrentGraphQuery = async (
  creator: Creator,
  baseGraphQuery: GraphQuery,
  {
    backend,
    graph,
    baseGraphQueryToCurrentGraphQuery,
    shouldPollQueries,
    forceRerender,
    setNetworkError,
  }: StateAndContext,
): Promise<GraphQuery | null> => {
  const graphQuery = await backend.runQueryAndPoll({
    // expect baseGraphQuery to be an Nhops or ClusterQuery, which in this case, can be typecasted as an Nhops Query
    query: baseGraphQuery.attributes.params as NHopsFromAccountQuery,
    graphId: graph.id,
    creatorId: creator.id,
    creatorType: creator.type,
    shouldContinue: () => shouldPollQueries.current,
    // populate baseGraphQueryToCurrentGraphQuery as soon as possible so the NavigationHeader can display
    // that the query is being processed
    onQuerySubmitted: (graphQuery: GraphQuery) => {
      baseGraphQueryToCurrentGraphQuery.current[baseGraphQuery.id] = graphQuery;
      forceRerender();
    },
    onNetworkError: () => {
      setNetworkError('We are experiencing network issues. Please reload to try again.');
    },
  });

  return graphQuery;
};

const runGraphQueryFromTemplate = async (
  creator: Creator,
  gqt: GraphQueryTemplate,
  accounts: Account[],
  { backend, graph, shouldPollQueries, setNetworkError }: StateAndContext,
): Promise<GraphQuery | null> => {
  const parameters: VariableValue[] = [];
  if (isNHopWithOneAccountVariable(gqt)) {
    // NOTE: gqt.payload.variables may be empty despite conditional referencing it (prob a bug),
    // so we search for the variable name within the conditionals
    let name = 'account';
    gqt.attributes.payload.conditionals.forEach((conditional) => {
      if (
        conditional.left === 'account' &&
        conditional.operator === Operator.Equals &&
        conditional.right[0].variable
      ) {
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        name = conditional.right[0].name;
      }
    });

    parameters.push({
      name: name,
      type: ValueType.Text,
      // at this point, we can assume accounts is nonempty.
      value: accounts[0].id,
    });
  }

  const graphQuery = await backend.queryWithTemplateAndPoll({
    graphQueryTemplateId: gqt.id,
    parameterMap: generateVariableMap(parameters),
    graphId: graph.id,
    creatorId: creator.id,
    creatorType: creator.type,
    shouldContinue: () => shouldPollQueries.current,
    onNetworkError: () => {
      setNetworkError('We are experiencing network issues. Please reload to try again.');
    },
  });

  return graphQuery;
};

export async function loadInitialGraphQueryInfoToCanvas(
  graphQueryInfo: GraphQueryInfo,
  {
    excludedNodes,
    g6Wrapper,
    graphExplorer,
    case: kase,
    module,
    setCurrentGraphQuery,
    setIsPerformingRelayout,
  }: StateAndContext,
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  handleUpdateExcludedNodes: (
    excludedNodes: NodeId[],
    hiddenNodes: NodeId[],
    visibleNodes: NodeId[],
  ) => void = null,
) {
  // currently loading graph data to canvas, set isPerformingRelayout to keep the loading spinner till relayout completes
  setIsPerformingRelayout(true);

  const message = graphExplorer.setData(
    graphQueryInfo.v2Data,
    new Map(graphQueryInfo.lightHydrationData),
  );
  g6Wrapper.setData(message);
  g6Wrapper.relayout();

  const { seedNodes } = graphQueryInfo.v2Data;
  if (seedNodes.length > 0) {
    graphExplorer.setSeedNodes(seedNodes.map((node) => node.id));
    graphExplorer.setExcludedNodes(excludedNodes);
    g6Wrapper.updateGraph(graphExplorer.computeExclusions());

    // If the fields are not initialized (don't have values), call handleUpdateExcludedNodes once to
    // set initial exclusion field values on the case.
    if (
      (handleUpdateExcludedNodes &&
        module['field-for-excluded-nodes'] &&
        kase?.attributes.fields[module['field-for-excluded-nodes']] == null) ||
      (module['field-for-hidden-nodes'] &&
        kase?.attributes.fields[module['field-for-hidden-nodes']] == null) ||
      (module['field-for-visible-nodes'] &&
        kase?.attributes.fields[module['field-for-visible-nodes']] == null)
    ) {
      handleUpdateExcludedNodes(
        graphExplorer.getExcludedNodes(),
        graphExplorer.getHiddenNodes(),
        graphExplorer.getVisibleNodes(),
      );
    }
  }
  graphExplorer.setCompleteHydrationData(new Map(graphQueryInfo.hydrationData));

  // TODO(bjon): setCurrentGraphQuery is updated specifically so that the exporter can refer to this
  // field when looking up the relevant graph query. Ideally, the exporter should be able to just do
  // { currentGraphQuery } = useQueryingProvider();.
  // To get rid of this, we will need to move more of the querying management into GraphQueryProvider
  setCurrentGraphQuery(graphQueryInfo.graphQuery);

  // Force a relayout, since the first layout is broken from an obscure reason in the
  // web workers.
  g6Wrapper.relayout();
}

export async function switchGraphQuery(
  newGraphQueryOptionId: string,
  newViewBaseQuery: boolean,
  {
    // context
    excludedNodes,
    graphExplorer,
    g6Wrapper,
    // state
    graphQueryOptionIdToGraphQuery,
    baseGraphQueryToCurrentGraphQuery,
    graphQueryToGraphQueryInfo,
    selectedGraphQueryOptionId,
    viewBaseQuery,
    // actions
    discardOngoingHydrations,
    setCurrentGraphQuery,
    setIsPerformingRelayout,
    setSelectedGraphQueryOptionId,
    setViewBaseQuery,
  }: StateAndContext,
) {
  // do not do anything if switching to itself
  if (newGraphQueryOptionId === selectedGraphQueryOptionId && newViewBaseQuery === viewBaseQuery) {
    return;
  }

  // sanity check that the GraphQueryInfo actually exists
  const newBaseQuery = graphQueryOptionIdToGraphQuery.current[newGraphQueryOptionId];
  const newGraphQuery = newViewBaseQuery
    ? newBaseQuery
    : baseGraphQueryToCurrentGraphQuery.current[newBaseQuery?.id];
  const newGraphQueryInfo = graphQueryToGraphQueryInfo.current[newGraphQuery?.id];
  if (newGraphQueryInfo == null) {
    return;
  }

  setIsPerformingRelayout(true);
  // get previous GraphQueryInfo
  const baseQuery = graphQueryOptionIdToGraphQuery.current[selectedGraphQueryOptionId];
  const graphQuery = viewBaseQuery
    ? baseQuery
    : baseGraphQueryToCurrentGraphQuery.current[baseQuery?.id];
  const prevGraphQueryInfo = graphQueryToGraphQueryInfo.current[graphQuery?.id];

  // before switching out, save the hydrationdata if viewing a current graph query
  if (prevGraphQueryInfo) {
    prevGraphQueryInfo.hydrationData = new Map(graphExplorer.getCompleteHydrationData());
  }

  const { seedNodes } = newGraphQueryInfo.v2Data;
  graphExplorer.switchNodesAndEdges(
    newGraphQueryInfo.v2Data.nodes,
    newGraphQueryInfo.v2Data.edges,
    newGraphQueryInfo.lightHydrationData,
    new Map(newGraphQueryInfo.hydrationData),
    seedNodes,
    g6Wrapper,
  );

  if (seedNodes.length > 0) {
    graphExplorer.setExcludedNodes(excludedNodes);
    g6Wrapper.updateGraph(graphExplorer.computeExclusions());
  }
  g6Wrapper.relayout();

  // TODO(bjon): setCurrentGraphQuery is updated specifically so that the exporter can refer to this
  // field when looking up the relevant graph query. Ideally, the exporter should be able to just do
  // { currentGraphQuery } = useQueryingProvider();.
  // To get rid of this, we will need to move more of the querying management into GraphQueryProvider
  setCurrentGraphQuery(newGraphQueryInfo.graphQuery);

  setSelectedGraphQueryOptionId(newGraphQueryOptionId);
  setViewBaseQuery(newViewBaseQuery);
  discardOngoingHydrations();

  // Force a relayout, since the first layout is broken from an obscure reason in the
  // web workers.
  g6Wrapper.relayout();
}

type GraphV2Props = PropsFromRedux & {
  accounts: Account[];
  caseQueryResults: CaseQueryResult[];
  module: ModuleGraphV2;
  handleUpdateFields: (fields: Record<string, any>, options?: { showToast?: boolean }) => void;
  handleRefreshCaseQueryResult: (caseQueryResult: CaseQueryResult) => void;
  attachedGraphQueries: GraphQuery[];
  graphQueryTemplates: GraphQueryTemplate[];
  graphSnapshots: GraphSnapshot[];
  isPreview: boolean;
  kase?: Case;
};

const GraphV2 = (props: GraphV2Props) => {
  const [hasPermissionsError, setHasPermissionsError] = useState<boolean>(false);

  const {
    accountStatuses,
    accountTypes,
    caseQueryResults,
    currentEnvironment,
    currentUser,
    graph,
    graphQueryTemplates,
    handleUpdateFields,
    handleRefreshCaseQueryResult,
    impersonationMode,
    kase,
    module: {
      'field-for-excluded-nodes': fieldForExcludedNodes,
      'field-for-hidden-nodes': fieldForHiddenNodes,
      'field-for-visible-nodes': fieldForVisibleNodes,
    },
  } = props;

  const isReadOnlyMode = impersonationMode === 'read_only';

  // Support both kebab case and not kebab case
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  let excludedNodes = kase?.attributes.fields[kebabCase(fieldForExcludedNodes)]
    ?.value as unknown as NodeId[] | undefined;
  if (!excludedNodes) {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    excludedNodes = kase?.attributes.fields[fieldForExcludedNodes]?.value as unknown as
      | NodeId[]
      | undefined;
  }

  const { loading: isLoadingGraphQueryTemplates } = useApiLoader({
    loader: () => {
      return props.loadGraphQueryTemplates();
    },
    deps: [],
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
        setHasPermissionsError(true);
      } else {
        props.showMessage(
          'Failed to load graph query templates, please reload to try again',
          MessageType.Failure,
        );
      }
    },
    onNetworkError: () => {
      props.showMessage(
        'We are experiencing network issues. Please reload to try again.',
        MessageType.Failure,
      );
    },
  });

  const { loading: isLoadingGraph } = useApiLoader({
    loader: () => {
      return props.loadGraphsAndGraph(props.currentEnvironment.id);
    },
    deps: [props.currentEnvironment],
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
        setHasPermissionsError(true);
      } else {
        props.showMessage('Failed to load graph, please reload to try again', MessageType.Failure);
      }
    },
    onNetworkError: () => {
      props.showMessage(
        'We are experiencing network issues. Please reload to try again.',
        MessageType.Failure,
      );
    },
  });

  const isLoading = isLoadingGraph || isLoadingGraphQueryTemplates;

  // --------------------------------------------------------------------------
  // Node exclusion handling
  // --------------------------------------------------------------------------

  const handleUpdateExcludedNodes = useCallback(
    (excludedNodes: NodeId[], hiddenNodes: NodeId[], visibleNodes: NodeId[]) => {
      if (isReadOnlyMode) {
        return;
      }

      handleUpdateFields(
        {
          ...(fieldForExcludedNodes
            ? {
                [fieldForExcludedNodes]: excludedNodes,
              }
            : {}),
          ...(fieldForHiddenNodes
            ? {
                [fieldForHiddenNodes]: hiddenNodes,
              }
            : {}),
          ...(fieldForVisibleNodes
            ? {
                [fieldForVisibleNodes]: visibleNodes,
              }
            : {}),
        },
        { showToast: false },
      );

      for (const caseQueryResult of caseQueryResults) {
        handleRefreshCaseQueryResult(caseQueryResult);
      }
    },
    [
      caseQueryResults,
      fieldForExcludedNodes,
      fieldForHiddenNodes,
      fieldForVisibleNodes,
      handleUpdateFields,
      handleRefreshCaseQueryResult,
    ],
  );
  const viewTypeSwitchEnabled = useFeatureFlag('dashboard:graph-pairwise-similarity-nodes');

  if (hasPermissionsError) {
    // Bail if the user doesn't have permissions, so that we don't
    // run keyboard listeners if/when there's no graph to interact with.
    return <></>;
  } else {
    return (
      <GraphDataWrapper
        accountStatuses={accountStatuses}
        accountTypes={accountTypes}
        currentEnvironment={currentEnvironment}
        currentUser={currentUser}
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        graph={graph}
        graphQueryTemplates={graphQueryTemplates}
      >
        <GraphCanvasContextProvider
          enabledModes={[Mode.PAN]}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          graph={graph}
          viewTypeSwitchEnabled={viewTypeSwitchEnabled}
          viewType={GraphViewTypes.Parsed}
        >
          <GraphConfigurationProvider>
            <GraphQueryingProvider showMessage={props.showMessage}>
              <PureGraphV2
                {...props}
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                excludedNodes={excludedNodes}
                handleUpdateExcludedNodes={handleUpdateExcludedNodes}
                isLoadingGraphQueryTemplates={isLoadingGraphQueryTemplates}
                isLoading={isLoading}
              />
            </GraphQueryingProvider>
          </GraphConfigurationProvider>
        </GraphCanvasContextProvider>
      </GraphDataWrapper>
    );
  }
};

const mapStateToProps = (state: RootState) => {
  return {
    accountStatuses: state.accountStatuses.accountStatuses,
    accountTypes: state.accountTypes.accountTypes,
    currentUser: state.currentUser.currentUser,
    currentEnvironment: state.currentEnvironment.currentEnvironment,
    graph: state.graph.graph,
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    graphQuery: state.graphQuery.graphQuery,
    graphQueryTemplates: state.graphQueryTemplates.graphQueryTemplates,
    graphSnapshots: state.case.caseToken
      ? state.case.case[state.case.caseToken].caseDetails?.graphSnapshots || []
      : [],
    impersonationMode: state.currentUser.impersonationMode,
  };
};

const mapDispatchToProps = {
  loadGraphsAndGraph,
  loadGraphQueryTemplates,
  runGraphQuery,
  showMessage,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(GraphV2);

const StyledButton = styled(Button)`
  border: 1px solid ${Color.CoreSlate100};
`;

interface PureGraphV2Props {
  accounts: Account[];
  accountStatuses: AccountStatus[];
  accountTypes: AccountType[];
  caseQueryResults: CaseQueryResult[];
  currentEnvironment: Environment;
  currentUser: User;
  excludedNodes: NodeId[];
  graph: Graph;
  attachedGraphQueries: GraphQuery[];
  graphQueryTemplates: GraphQueryTemplate[];
  graphSnapshots: GraphSnapshot[];
  handleUpdateExcludedNodes: (
    excludedNodes: NodeId[],
    hiddenNodes: NodeId[],
    visibleNodes: NodeId[],
  ) => void;
  isLoadingGraphQueryTemplates: boolean;
  // TODO-before-merge: verify this is needed
  isLoading: boolean;
  isPreview: boolean;
  kase?: Case;
  module: ModuleGraphV2;
}

const PureGraphV2 = ({
  accounts,
  accountTypes,
  excludedNodes,
  graph,
  graphSnapshots,
  attachedGraphQueries,
  handleUpdateExcludedNodes,
  isLoadingGraphQueryTemplates,
  isLoading,
  isPreview,
  kase,
  module,
}: PureGraphV2Props) => {
  const isMaintenanceMode = useFeatureFlag('dashboard:graph-maintenance-mode');
  const isGraphEnabled = useProductFeature(ProductFeatureName.Graph);
  const backend = useBackend();
  const { graphExplorer } = useGraphExplorer();
  const g6Wrapper = useG6Wrapper();
  const { discardOngoingHydrations, setCurrentGraphQuery } = useGraphQuerying();
  const { graphQueryTemplates } = useGraphData();

  // nonce used to force a rerender upon relevant updates to the graph query MutableRef objects
  // TODO: (bjon) a nonce feels gross here, replace with some reducer
  const [_updateNonce, setUpdateNonce] = useState<number>(0);
  const forceRerender = () => {
    setUpdateNonce((n) => n + 1);
  };
  const [isPerformingRelayout, setIsPerformingRelayout] = useState<boolean>(false);
  // Network errors cause the entire graph card to be disabled so we stop processing GQs.
  // graph killswitch being enabled is an example of a network error
  const [networkError, setNetworkError] = useState<string | null>(null);

  // shouldPollQueries is used for polling new graph queries generated from a GQT
  // or polling current graph queries from an attached base query. Used to cancel graph queries upon an unmount.
  const shouldPollQueries = useRef<boolean>(true);

  const [validAttachedGraphQueries, setValidAttachedGraphQueries] = useState<GraphQuery[] | null>(
    null,
  );
  const [gqtsToGenerateGraphQueriesFor, setGqtsToGenerateGraphQueriesFor] = useState<
    GraphQueryTemplate[] | null
  >(null);
  const graphQueryToGraphQueryInfo = useRef<{ [token: string]: GraphQueryInfo }>({});

  // A GraphQueryOption refers to either an attached GQ or GQT that the user can switch between.
  // The graphQueryOptionId is the token of the GQ/GQT. graphQueryOptionIdToGraphQuery is there to map
  // the GQT to the corresponding GQ.
  const graphQueryOptionIdToGraphQuery = useRef<{ [token: string]: GraphQuery }>({});
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const [selectedGraphQueryOptionId, setSelectedGraphQueryOptionId] = useState<string>(null);
  // For attached GQs, we may toggle between viewing the query itself (base query) and viewing the same query
  // ran just now (currentGraphQuery). We use viewBaseQuery to keep track of what is selected.
  // viewBaseQuery is true if a GQT is selected.
  const [viewBaseQuery, setViewBaseQuery] = useState<boolean>(true);
  const baseGraphQueryToCurrentGraphQuery = useRef<{ [token: string]: GraphQuery }>({});

  const selectedBaseQuery: GraphQuery | undefined =
    graphQueryOptionIdToGraphQuery.current[selectedGraphQueryOptionId];
  const selectedGraphQuery: GraphQuery | undefined = viewBaseQuery
    ? selectedBaseQuery
    : baseGraphQueryToCurrentGraphQuery.current[selectedBaseQuery?.id];
  const selectedGraphQueryInfo: GraphQueryInfo | undefined =
    graphQueryToGraphQueryInfo.current[selectedGraphQuery?.id];

  // Build a context object used across method calls
  // TODO: This object may actually not be necessary but as I was refactoring, state dependencies
  // across methods were already so nested that it was difficult to pull things out.
  //
  // Moving a lot of the state objects to `useReducer` should help clean this up and simplify
  // some of the reactive function calls in this module.
  const stateAndContext: StateAndContext = {
    // context
    backend,
    case: kase,
    excludedNodes,
    g6Wrapper,
    graph,
    graphExplorer,
    graphSnapshots,
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    graphQueryTemplates,
    module: module as unknown as ModuleGraphV2,
    // state
    graphQueryOptionIdToGraphQuery,
    baseGraphQueryToCurrentGraphQuery,
    graphQueryToGraphQueryInfo,
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    validAttachedGraphQueries,
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    gqtsToGenerateGraphQueriesFor,
    selectedGraphQueryOptionId,
    viewBaseQuery,
    shouldPollQueries,
    // actions
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    discardOngoingHydrations,
    forceRerender,
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    setCurrentGraphQuery,
    setIsPerformingRelayout,
    setSelectedGraphQueryOptionId,
    setViewBaseQuery,
    setNetworkError,
  };

  // --------------------------------------------------------------------------
  // Hooks
  // --------------------------------------------------------------------------
  useEffect(() => {
    if (!g6Wrapper) {
      return;
    }
    g6Wrapper.setHandleLayoutComplete(() => {
      setIsPerformingRelayout(false);
    });
  }, [g6Wrapper]);

  // stop polling graph queries on unmount
  useEffect(() => {
    return () => {
      shouldPollQueries.current = false;
    };
  }, []);

  // determine valid Graph Query Case Objects and Graph Query Templates to generate GQS for
  const [hasDeterminedQueriesToRun, setHasDeterminedQueriesToRun] = useState(false);
  useEffect(() => {
    if (hasDeterminedQueriesToRun) {
      return;
    }
    // don't bother processing queries if not supposed to
    if (isMaintenanceMode || isPreview || !isGraphEnabled) {
      return;
    }
    // while loading, graphQueryTemplates is [], so we are forced to check this loading flag
    if (isLoadingGraphQueryTemplates) {
      return;
    }

    setHasDeterminedQueriesToRun(true);

    const { 'expected-graph-query-template': expectedGraphQueryTemplate } = module;
    const graphQueryTemplatesFilter = Array.isArray(expectedGraphQueryTemplate)
      ? expectedGraphQueryTemplate
      : [expectedGraphQueryTemplate].filter(Boolean);

    // If no expectedGraphQueryTemplates set, then show all attached Graph Query Case Objects
    // Otherwise, filter only graph queries created from a template in expectedGraphQueryTemplates
    let relevantAttachedGraphQueries: GraphQuery[] = [];
    if (graphQueryTemplatesFilter.length > 0) {
      relevantAttachedGraphQueries = attachedGraphQueries.filter((graphQuery) => {
        return graphQueryTemplatesFilter.includes(
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          graphQuery.relationships['graph-query-template']?.data?.id,
        );
      });
    } else {
      relevantAttachedGraphQueries = attachedGraphQueries;
    }
    relevantAttachedGraphQueries.forEach((attachedGraphQuery: GraphQuery) => {
      graphQueryOptionIdToGraphQuery.current[attachedGraphQuery.id] = attachedGraphQuery;
    });
    setValidAttachedGraphQueries(relevantAttachedGraphQueries);

    // find all expected GQTs without an associated Attached Graph Query Case Object
    // plan to generate a graph query for them if possible
    const gqtsWithoutCaseObject = graphQueryTemplatesFilter
      .map((gqtId) => {
        if (
          attachedGraphQueries.some(
            (gq) => gq.relationships['graph-query-template']?.data?.id === gqtId,
          )
        ) {
          return null;
        }
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        return graphQueryTemplates.find((gqt) => gqt.id === gqtId);
      })
      .filter(Boolean);

    const gqtsWithoutCaseObjectThatCanBeRun = getRunnableGraphQueryTemplates(
      gqtsWithoutCaseObject as GraphQueryTemplate[],
    );

    setGqtsToGenerateGraphQueriesFor(gqtsWithoutCaseObjectThatCanBeRun);

    // determine initial graph query option to display; prefer showing attached graph queries over gqts
    if (relevantAttachedGraphQueries.length > 0) {
      setSelectedGraphQueryOptionId(relevantAttachedGraphQueries[0].id);
    } else if (gqtsWithoutCaseObjectThatCanBeRun.length > 0) {
      setSelectedGraphQueryOptionId(gqtsWithoutCaseObjectThatCanBeRun[0].id);
    }
  }, [
    module['expected-graph-query-template'],
    attachedGraphQueries,
    accounts,
    isLoadingGraphQueryTemplates,
  ]);

  // generate GraphQueryInfos for all attached graph queries
  useEffect(() => {
    if (validAttachedGraphQueries == null) {
      return;
    }

    validAttachedGraphQueries.forEach(async (graphQuery: GraphQuery) => {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      const attachedGraphSnapshot: GraphSnapshot | null = graphSnapshots.find(
        (graphSnapshot) => graphQuery.id === graphSnapshot.relationships['graph-query'].data.id,
      );

      // generate query info if it does not exist
      if (graphQueryToGraphQueryInfo.current[graphQuery.id] == null) {
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        graphQueryToGraphQueryInfo.current[graphQuery.id] = await generateGraphQueryInfo(
          graphQuery,
          attachedGraphSnapshot,
          stateAndContext,
        );
        forceRerender();
      }
    });
  }, [validAttachedGraphQueries]);

  // generate queries for all gqts to build gqs off of
  useEffect(() => {
    if (gqtsToGenerateGraphQueriesFor == null) {
      return;
    }

    gqtsToGenerateGraphQueriesFor.forEach(async (graphQueryTemplate: GraphQueryTemplate) => {
      const creator = stateAndContext.case
        ? { type: 'Case', id: stateAndContext.case?.id }
        : { type: 'Account', id: accounts[0].id };
      const graphQueryFromTemplate = await runGraphQueryFromTemplate(
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        creator,
        graphQueryTemplate,
        accounts,
        stateAndContext,
      );
      // graphQueryFromTemplate may be null if, the query cancelled upon leaving the page, for example
      if (graphQueryFromTemplate == null) {
        return;
      }
      graphQueryOptionIdToGraphQuery.current[graphQueryTemplate.id] = graphQueryFromTemplate;
      const graphQueryInfo = await generateGraphQueryInfo(
        graphQueryFromTemplate,
        null,
        stateAndContext,
      );

      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      graphQueryToGraphQueryInfo.current[graphQueryFromTemplate.id] = graphQueryInfo;
      forceRerender();
    });
  }, [gqtsToGenerateGraphQueriesFor]);

  // render the initial selected graph query onto the graph canvas.
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  useEffect(() => {
    if (!isInitialLoad) {
      return;
    }
    // dont set graph query if g6 not loaded, or graph query info does not exist
    if (selectedGraphQueryInfo == null) {
      return;
    }

    setIsInitialLoad(false);
    loadInitialGraphQueryInfoToCanvas(selectedGraphQueryInfo, stateAndContext);
  }, [selectedGraphQueryInfo]);

  // This is triggered upon changing the graphQueryOption from the selector
  // If the graphQueryOption is from a case object, initiate a current graph query view
  // keep track of which base queries we initiated a graph query for, so we don't do extra work
  const baseQueriesCurrentlyPollingForCurrentGraphQuery = useRef<Set<string>>(new Set());
  useEffect(() => {
    const populateGraphQueryInfo = async () => {
      if (selectedGraphQueryOptionId == null) {
        return;
      }

      const baseQuery = graphQueryOptionIdToGraphQuery.current[selectedGraphQueryOptionId];
      if (baseQuery == null) {
        return;
      } // this should not happen

      if (isGraphQueryCaseObjectOption(selectedGraphQueryOptionId)) {
        // This is a very rare case in which the case is created and viewed before the graph query completed.
        // This usually indicates a bad workflow layout, do not try and load the graph query
        if (baseQuery.attributes.status === GraphQueryStatus.Submitted) {
          return;
        }

        const attachedGraphSnapshot = graphSnapshots.find(
          (graphSnapshot: GraphSnapshot) =>
            baseQuery.id === graphSnapshot.relationships['graph-query'].data.id,
        );

        // generate query info if it does not exist
        if (graphQueryToGraphQueryInfo.current[baseQuery.id] == null) {
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          graphQueryToGraphQueryInfo.current[baseQuery.id] = await generateGraphQueryInfo(
            baseQuery,
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            attachedGraphSnapshot,
            stateAndContext,
          );
        }

        // get current graph query result and GraphQueryInfo
        if (
          baseGraphQueryToCurrentGraphQuery.current[baseQuery.id] == null &&
          !baseQueriesCurrentlyPollingForCurrentGraphQuery.current.has(baseQuery.id)
        ) {
          baseQueriesCurrentlyPollingForCurrentGraphQuery.current.add(baseQuery.id);
          const currentGraphQuery = await getCurrentGraphQuery(
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            { type: 'Case', id: stateAndContext.case.id },
            baseQuery,
            stateAndContext,
          );
          // currentGraphQuery may be null if, the query cancelled upon leaving the page, for example
          if (currentGraphQuery == null) {
            return;
          }
          baseGraphQueryToCurrentGraphQuery.current[baseQuery.id] = currentGraphQuery;
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          graphQueryToGraphQueryInfo.current[currentGraphQuery.id] = await generateGraphQueryInfo(
            currentGraphQuery,
            null,
            stateAndContext,
          );
          forceRerender();
        }
      }
    };
    populateGraphQueryInfo();
  }, [selectedGraphQueryOptionId]);

  const { excludedNodesVisible } = useGraphExplorer();
  const toggleExcludedNodesVisibility = useCallback(() => {
    let updateMessage;
    if (excludedNodesVisible) {
      updateMessage = graphExplorer.hideExcludedNodes();
    } else {
      updateMessage = graphExplorer.showExcludedNodes();
    }
    g6Wrapper.updateGraph(updateMessage);
  }, [excludedNodesVisible]);

  // --------------------------------------------------------------------------
  // Loading and Overlay Messages
  // --------------------------------------------------------------------------
  const isLoadingGraphQuery =
    isLoading ||
    isPerformingRelayout ||
    attachedGraphQueries == null ||
    selectedGraphQueryInfo == null;
  const loadingGraphEnvironment = graph == null || isLoadingGraphQuery;
  const previewMessage = isPreview
    ? kase
      ? 'Graph results will appear here after the first Workflow run'
      : 'Graph results will appear here'
    : null;
  let redactedMessage = null;
  const graphQueryRedacted =
    selectedGraphQueryInfo?.graphQuery?.attributes?.['redacted-at'] != null;
  if (graphQueryRedacted) {
    redactedMessage = 'Graph results have been redacted';
  }

  let errorMessage: string | null = networkError;
  if (selectedGraphQuery) {
    switch (selectedGraphQuery.attributes.status) {
      case GraphQueryStatus.Errored:
        errorMessage = 'Graph Query has errored. Please reload to try again.';
        break;
      case GraphQueryStatus.TimedOut:
        errorMessage = 'Graph Query has timed out. Please reload to try again.';
        break;
      case GraphQueryStatus.Submitted:
        // This is a very rare case in which the case is created and viewed before the graph query completed.
        // This usually indicates a bad workflow layout, do not try and load the graph query
        errorMessage = 'Workflow Graph Query is still processing. Please reload to try again';
    }
  }
  if (
    hasDeterminedQueriesToRun &&
    validAttachedGraphQueries?.length === 0 &&
    gqtsToGenerateGraphQueriesFor?.length === 0
  ) {
    errorMessage = stateAndContext?.case
      ? 'No Graph Query or Valid Graph Query Template attached to the Card'
      : 'No Graph Query Template attached to the Card';
  }

  const overlayMessage = previewMessage || errorMessage || redactedMessage;

  // --------------------------------------------------------------------------
  // Open in Graph Explorer Button
  // --------------------------------------------------------------------------
  const toGraphExplorerLink = selectedGraphQuery
    ? `/dashboard/explorer?queryId=${selectedGraphQuery.id}`
    : null;
  const canvasButtons = (
    <>
      <Tooltip
        iconTrigger={
          <Link to={toGraphExplorerLink || ''}>
            <StyledButton
              type='button'
              width='icon'
              size='sm'
              variant='inverse'
              disabled={toGraphExplorerLink == null}
            >
              <GraphIcon />
            </StyledButton>
          </Link>
        }
        portal={true}
        side='top'
      >
        <Paragraph color='primary'>Open in Graph Explorer</Paragraph>
      </Tooltip>
      <Tooltip
        iconTrigger={
          <StyledButton
            type='button'
            width='icon'
            size='sm'
            variant='inverse'
            onClick={toggleExcludedNodesVisibility}
          >
            {excludedNodesVisible ? <EyeOffIcon /> : <EyeIcon />}
          </StyledButton>
        }
        portal={true}
        side='top'
      >
        <Paragraph color='primary'>Toggle Excluded Nodes Visibility</Paragraph>
      </Tooltip>
    </>
  );

  const isModuleDynamicHeight = module['module-layout']['dynamic-height'];
  return !isMaintenanceMode && isGraphEnabled ? (
    <GraphV2Card
      accountTypes={accountTypes}
      nodeFunctionsConfigs={graph?.attributes['node-functions-configs'] || []}
      canvasButtons={canvasButtons}
      data-test={`module__graph--${(module as unknown as ModuleGraphV2).token}`}
      enableScroll={!isModuleDynamicHeight}
      handleUpdateExcludedNodes={handleUpdateExcludedNodes}
      isLoading={loadingGraphEnvironment}
      graphQuerySelector={
        gqtsToGenerateGraphQueriesFor &&
        validAttachedGraphQueries && (
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          <GraphQueryOptionSelector
            stateAndContext={stateAndContext}
            switchGraphQuery={(optionId: string) => {
              switchGraphQuery(optionId, true, stateAndContext);
            }}
          />
        )
      }
      navigationHeader={
        <NavigationHeader
          stateAndContext={stateAndContext}
          onSwitch={(newViewBaseQuery: boolean) => {
            switchGraphQuery(selectedGraphQueryOptionId, newViewBaseQuery, stateAndContext);
          }}
        />
      }
      overlayMessage={overlayMessage}
    />
  ) : (
    <Placeholder title='Graph' message='Graph not enabled' />
  );
};
